import moment from "moment";

export const isTodayDate = (date: Date | string) => {
  return moment().format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD");
};

export const isTomorrowDate = (date: Date | string) => {
  return (
    moment().add(1, "day").format("YYYY-MM-DD") ===
    moment(date).format("YYYY-MM-DD")
  );
};

export const getCurrentDate = () => {
  return moment().format("YYYY-MM-DD");
};

export const getCurrentTime = () => {
  return moment().format("HH:mm:ss");
};

export const dateToString = (date?: Date | null | string) => {
  if (!date) return "";
  return moment(date).format("DD-MM-YYYY");
};

export const dateForDatabase = (date?: Date | null) => {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DD");
};

export const dateAndTimeForDatabase = (date?: string | Date | null) => {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DD HH:mm");
};

export const getDateAndTime = (
  date?: Date | null | string,
  invalidDateLabel = "",
) => {
  if (!date) return invalidDateLabel;
  const dateMoment = moment(date).format("DD-MM-YYYY HH:mm");
  if (dateMoment === "Invalid date") {
    return invalidDateLabel || dateMoment;
  }
  return dateMoment;
};

export const getHoursAndMinutes = (date?: Date | null) => {
  if (!date) return "";
  return moment(date).format("HH:mm");
};

export const getWeekDays = (locale: string) => {
  const baseDate = new Date(Date.UTC(2017, 0, 2)); // just a Monday
  const weekDays = [];
  for (let i = 1; i <= 7; i++) {
    weekDays.push({
      id: i,
      name: baseDate.toLocaleDateString(locale, { weekday: "long" }),
    });
    baseDate.setDate(baseDate.getDate() + 1);
  }
  return weekDays;
};

export const getDateWithoutNthMonths = (months: number) => {
  return new Date(new Date().setMonth(new Date().getMonth() - months));
};

export const getDateWithoutNthDays = (day: number) => {
  return moment().subtract(day, "days").toDate();
};

export const addDaysToDate = (
  date?: Date | string | null,
  days = 0,
): Date | null => {
  if (!date || !moment(date).isValid()) return null;

  return moment(date).add(days, "days").toDate();
};

// when you run this function you should put date like this "getStartFromSpecificDate("01 April, 2024")" "
export const getStartFromSpecificDate = (date?: Date | null | string) => {
  return moment(date).toDate();
};

export const getLastDayOfMonth = (
  date: Date | string,
  withFullDate?: boolean,
) => {
  const mask = withFullDate ? "YYYY-MM-DD " : "DD";
  const endOfMonth = moment(date).endOf("month").format(mask);
  return endOfMonth;
};

export const getFirstDayOfMonth = (
  date: Date | string,
  withFullDate?: boolean,
) => {
  const mask = withFullDate ? "YYYY-MM-DD " : "DD";
  const startOfMonth = moment(date).startOf("month").format(mask);
  return startOfMonth;
};

export const getWeekDaysByNumber = (
  days?: number | number[] | null,
): string => {
  if (!days) return "";
  const values = Array.isArray(days) ? days : [days];

  if (values.some((day) => Number(day) === 0 || Number(day) === 10)) {
    return "Toate zilele";
  }

  const weekDays: Record<number, string> = {
    1: "Lu",
    2: "Ma",
    3: "Mi",
    4: "Jo",
    5: "Vi",
    6: "Sâ",
    7: "Du",
  };

  return values
    .map((day) => weekDays[day])
    .filter(Boolean)
    .join(", ");
};

export const getWeekDayNumber = (
  date?: Date | string | null,
): number | null => {
  if (!date || !moment(date).isValid()) return null;

  return moment(date).isoWeekday();
};

export const isDateInWeekDayProgram = (
  date?: Date | string | null,
  program?: number | number[] | null,
): boolean => {
  const weekDayNumber = getWeekDayNumber(date);
  if (!weekDayNumber || program === undefined || program === null) return false;

  const programDays = (Array.isArray(program) ? program : [program]).map(
    Number,
  );

  return (
    programDays.includes(0) ||
    programDays.includes(10) ||
    programDays.includes(weekDayNumber)
  );
};

export const getDaysInYear = (year: number) => {
  if (!Number.isInteger(year) || year < 1 || year > 9999) return null;
  return moment({ year }).isLeapYear() ? 366 : 365;
};

export const getDateFromJulianDay = (year: number, day: number) => {
  const daysInYear = getDaysInYear(year);
  if (!daysInYear || !Number.isInteger(day) || day < 0 || day >= daysInYear) {
    return null;
  }
  return moment({ year, month: 0, date: 1 }).add(day, "days").toDate();
};

// AWB/BIN use PHP's date('z'): January 1 is day 000.
export const getJulianDateInfo = (date?: Date | string | null) => {
  if (!date) return null;
  const parsed = moment(date);
  if (!parsed.isValid()) return null;
  const day = parsed.dayOfYear() - 1;
  const formattedDay = String(day).padStart(3, "0");
  return {
    year: parsed.year(),
    day,
    formattedDay,
    code: `${parsed.format("YY")}${formattedDay}`,
  };
};

export function getTodayInfo() {
  const today = moment();
  return {
    dayOfYear: today.dayOfYear(),
    formattedDate: today.format("DD-MM-YYYY"),
  };
}
